import React, { useState, useMemo } from 'react';
import { Play, Youtube } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Reveal } from '../hooks/useReveal';
import { SectionTitle } from './About';
import { videos, ytThumb, assets } from '../mock';
import { Dialog, DialogContent } from './ui/dialog';

const VideoGallery = () => {
  const { t, lang } = useLanguage();
  const [filter, setFilter] = useState('all');
  const [active, setActive] = useState(null);

  const f = t.videosSection.filters;
  const filters = [['all', f.all], ['weddings', f.weddings], ['cultural', f.cultural], ['singer', f.singer], ['promo', f.promo]];
  const shown = useMemo(() => (filter === 'all' ? videos : videos.filter((v) => v.cat === filter)), [filter]);

  return (
    <section id="videos" className="section-pad bg-[#14120d]">
      <div className="max-w-6xl mx-auto">
        <Reveal>
          <SectionTitle eyebrow={t.videosSection.eyebrow} title={t.videosSection.title} subtitle={t.videosSection.subtitle} />
        </Reveal>

        <div className="flex flex-wrap items-center justify-center gap-3 mt-10">
          {filters.map(([key, label]) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`px-5 py-2 text-[13px] tracking-[0.14em] uppercase font-sans-el rounded-sm border transition-all ${
                filter === key ? 'bg-[#e6b83f] text-[#14120d] border-[#e6b83f]' : 'border-[#e6b83f]/30 text-[#d8d1c1] hover:border-[#e6b83f]/70'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {shown.map((v, i) => (
            <Reveal key={v.id} delay={(i % 3) * 80}>
              <button
                onClick={() => setActive(v.id)}
                className="group relative w-full aspect-video overflow-hidden rounded-sm border border-[#e6b83f]/20 hover:border-[#e6b83f]/60 transition-colors"
              >
                <img src={ytThumb(v.id)} alt={v.title[lang]} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <span className="absolute inset-0 bg-black/30 group-hover:bg-black/15 transition-colors" />
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="w-14 h-14 rounded-full bg-[#e6b83f]/90 flex items-center justify-center text-[#14120d] group-hover:scale-110 transition-transform">
                    <Play size={22} fill="currentColor" />
                  </span>
                </span>
                <span className="absolute bottom-0 left-0 right-0 p-4 text-left bg-gradient-to-t from-black/80 to-transparent">
                  <span className="font-deco text-sm text-[#f7f1e3]">{v.title[lang]}</span>
                </span>
              </button>
            </Reveal>
          ))}
        </div>

        <div className="text-center mt-12">
          <a href={assets.youtubeChannel} target="_blank" rel="noreferrer" className="btn-ghost-gold inline-flex items-center gap-2 px-7 py-3.5 rounded-sm text-[13px] tracking-[0.2em] uppercase">
            <Youtube size={17} /> {t.videosSection.more}
          </a>
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-4xl bg-transparent border-0 p-0 shadow-none">
          {active && (
            <div className="aspect-video w-full">
              <iframe
                className="w-full h-full rounded-sm"
                src={`https://www.youtube.com/embed/${active}?autoplay=1`}
                title="LAJO"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default VideoGallery;
